import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import type { Amenity } from '@/types/v2/amenity';
import { getAmenities } from '@/api/amenities';
import { getBlockedSlots } from '@/api/reservations';
import { useUser } from '@/hooks/v2/use-user';
import { effectiveCommunityRoles } from '@/lib/nav-access';

import { ReservationDetailsDialog } from './reservation-details-dialog';

interface ReservationsCalendarProps {
  onReserve: (amenityId: string) => void;
}

export function ReservationsCalendar({ onReserve }: ReservationsCalendarProps): React.JSX.Element {
  const { community, user } = useUser();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const calendarRef = React.useRef<FullCalendar>(null);
  const [selectedAmenityId, setSelectedAmenityId] = React.useState<string>('all');
  const [selectedReservation, setSelectedReservation] = React.useState<any>(null);
  const [showReservationDetailsDialog, setShowReservationDetailsDialog] = React.useState(false);

  const { data: amenitiesData, isLoading: isAmenitiesLoading } = useQuery({
    queryKey: ['amenities', community?.id, 'active'],
    queryFn: () => getAmenities(community?.id || '', { activeOnly: true }),
    enabled: !!community?.id,
  });

  const amenities = amenitiesData?.amenities ?? [];

  const isManager = React.useMemo(() => {
    if (!user || !community) return false;
    const accountRoles = user.account_roles || [];
    const hasAccountRole = accountRoles.some((role) =>
      ['account_owner', 'account_admin', 'account_manager'].includes(role.name),
    );
    // Board members see the same detail: clicking the slot already opens the
    // manager dialog for them, so hiding names/legend here was inconsistent.
    const communityRoles = effectiveCommunityRoles(community.roles);
    const hasCommunityRole = communityRoles.includes('community_manager');
    return hasAccountRole || hasCommunityRole;
  }, [user, community]);

  const handleEventClick = (clickInfo: any) => {
    const eventId: string = clickInfo.event.id;
    // Event IDs are "blocked-{uuid}". Strip the prefix and match by uuid.
    if (eventId.startsWith('blocked-')) {
      const uuid = eventId.slice('blocked-'.length);
      const reservation = blockedSlotsData?.find((slot) => slot.uuid === uuid);
      if (reservation) {
        setSelectedReservation(reservation);
        setShowReservationDetailsDialog(true);
      }
    }
  };

  const { data: blockedSlotsData, isLoading: isBlockedSlotsLoading } = useQuery({
    queryKey: ['blocked-slots-all', community?.id],
    queryFn: async () => {
      if (!community?.id || !amenities.length) return [];
      const start = dayjs().startOf('month').subtract(1, 'month').format('YYYY-MM-DD');
      const end = dayjs().endOf('month').add(2, 'months').format('YYYY-MM-DD');

      const allBlockedSlots = [];
      for (const amenity of amenities) {
        try {
          const response = await getBlockedSlots(amenity.id, start, end);
          const slots = response.blocked_slots || [];
          const slotsWithAmenity = slots.map((slot) => ({
            ...slot,
            amenity_name: amenity.name,
            amenity_id: amenity.id,
          }));
          allBlockedSlots.push(...slotsWithAmenity);
        } catch {
          // Skip failing amenities
        }
      }
      return allBlockedSlots;
    },
    enabled: Boolean(community?.id) && amenities.length > 0,
  });

  const events = React.useMemo(() => {
    if (!blockedSlotsData) return [];

    return blockedSlotsData
      .filter((slot) => selectedAmenityId === 'all' || String(slot.amenity_id) === selectedAmenityId)
      .map((slot) => {
        const isApproved = slot.status === 'approved';
        const isPending = slot.status === 'pending';
        const isMine = slot.is_mine;
        const amenityName = slot.amenity_name || 'Unknown';

        let color = theme.palette.primary.main;
        let title = amenityName;

        if (isMine) {
          if (isPending) {
            color = theme.palette.warning.main;
            title += ' (Pending)';
          } else {
            title += ' (You)';
          }
        } else if (isManager && slot.user) {
          const userName = `${slot.user.first_name} ${slot.user.last_name}`;
          if (isPending) {
            color = theme.palette.info.main;
            title += ` · ${userName} · Pending`;
          } else {
            title += ` · ${userName}`;
          }
        } else {
          if (isPending) {
            color = 'rgba(148, 163, 184, 0.5)'; // neutral slate, works in light + dark
            title += ' · Pending';
          } else {
            title += ' · Reserved';
          }
        }

        return {
          id: `blocked-${slot.uuid ?? slot.id}`,
          title,
          start: dayjs.utc(slot.start_time).local().toISOString(),
          end: dayjs.utc(slot.end_time).local().toISOString(),
          display: 'block',
          color,
          textColor: isMine || isManager ? undefined : theme.palette.getContrastText(color),
        };
      });
  }, [blockedSlotsData, theme, isManager, selectedAmenityId]);

  if (isAmenitiesLoading) {
    return <Skeleton variant="rounded" height={500} sx={{ borderRadius: 3 }} />;
  }

  if (!amenities.length) {
    return (
      <Paper
        elevation={0}
        sx={{ py: 8, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          No Amenities Available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Your community has not set up any amenities for reservation yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 700 }}>Check availability</Typography>
          <Typography color="text.secondary" variant="body2">
            Choose an amenity, review the calendar, then start a reservation when you find a time that works.
          </Typography>
        </Stack>

        {/* Legend */}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            size="small"
            label="Approved"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
          <Chip
            size="small"
            label="Your Pending"
            sx={{
              bgcolor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
          {isManager && (
            <Chip
              size="small"
              label="Others Pending"
              sx={{
                bgcolor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          )}
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' },
        }}
      >
        <AmenityRail
          amenities={amenities}
          onReserve={onReserve}
          onSelectAmenity={setSelectedAmenityId}
          selectedAmenityId={selectedAmenityId}
        />

        {/* Calendar */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            overflow: 'hidden',
            p: { xs: 1, md: 2.5 },
            minWidth: 0,
          }}
        >
          {isBlockedSlotsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box
              sx={{
                '& .fc': { fontSize: '0.8rem' },
                '& .fc-toolbar-title': { fontSize: '1.1rem !important', fontWeight: 600 },
                '& .fc-button-primary': {
                  bgcolor: 'transparent !important',
                  color: `${theme.palette.text.primary} !important`,
                  border: `1px solid ${theme.palette.divider} !important`,
                  borderRadius: '6px !important',
                  fontSize: '0.8rem !important',
                  fontWeight: 500,
                  textTransform: 'capitalize',
                  '&:hover': {
                    bgcolor: `rgba(var(--mui-palette-primary-mainChannel) / 0.08) !important`,
                  },
                  '&.fc-button-active': {
                    bgcolor: `${theme.palette.primary.main} !important`,
                    color: `${theme.palette.primary.contrastText} !important`,
                    borderColor: `${theme.palette.primary.main} !important`,
                  },
                },
                '& .fc-daygrid-day-number, & .fc-col-header-cell-cushion': {
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                },
                '& .fc-event': {
                  borderRadius: '4px !important',
                  border: 'none !important',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                },
                '& .fc-timegrid-slot': {
                  height: '2.5em',
                },
                '& .fc-scrollgrid': {
                  borderColor: `${theme.palette.divider} !important`,
                },
                '& .fc-scrollgrid td, & .fc-scrollgrid th': {
                  borderColor: `${theme.palette.divider} !important`,
                },
                '& .fc-day-today': {
                  bgcolor: `rgba(var(--mui-palette-primary-mainChannel) / 0.04) !important`,
                },
              }}
            >
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay',
                }}
                initialView="timeGridWeek"
                editable={false}
                dayMaxEvents
                weekends
                events={events}
                eventClick={handleEventClick}
                height="auto"
                allDaySlot={false}
                slotMinTime="06:00:00"
                slotMaxTime="23:00:00"
                nowIndicator
                eventDisplay="block"
              />
            </Box>
          )}
        </Paper>
      </Box>

      {community?.amenity_reservations_description && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {community.amenity_reservations_description}
          </Typography>
        </Paper>
      )}

      <ReservationDetailsDialog
        open={showReservationDetailsDialog}
        reservation={selectedReservation}
        onClose={() => {
          setShowReservationDetailsDialog(false);
          setSelectedReservation(null);
        }}
        onSuccess={() => {
          setShowReservationDetailsDialog(false);
          setSelectedReservation(null);
          queryClient.invalidateQueries({ queryKey: ['blocked-slots-all', community?.id] });
        }}
      />
    </Stack>
  );
}

function AmenityRail({
  amenities,
  selectedAmenityId,
  onSelectAmenity,
  onReserve,
}: {
  amenities: Amenity[];
  selectedAmenityId: string;
  onSelectAmenity: (amenityId: string) => void;
  onReserve: (amenityId: string) => void;
}): React.JSX.Element {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        overflow: 'hidden',
        alignSelf: { lg: 'start' },
        position: { lg: 'sticky' },
        top: { lg: 16 },
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontWeight: 700 }}>Reservable amenities</Typography>
        <Typography color="text.secondary" variant="body2">
          Filter the calendar or start a reservation.
        </Typography>
      </Box>

      <Stack sx={{ p: 1.25 }} spacing={1}>
        <Button
          fullWidth
          onClick={() => onSelectAmenity('all')}
          variant={selectedAmenityId === 'all' ? 'contained' : 'outlined'}
          sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 1.5 }}
        >
          All amenities
        </Button>

        {amenities.map((amenity) => {
          const isSelected = selectedAmenityId === String(amenity.id);
          const deposit = Number(amenity.deposit_amount || 0);
          const totalFee = Number(amenity.total_fee_amount ?? amenity.fee_amount ?? 0);
          const feeLabel = [
            deposit > 0 ? `$${deposit.toFixed(0)} deposit` : null,
            totalFee > 0 ? `$${totalFee.toFixed(0)} total` : null,
          ].filter(Boolean).join(' · ');

          return (
            <Box
              key={amenity.id}
              sx={{
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: isSelected ? 'rgba(var(--mui-palette-primary-mainChannel) / 0.06)' : 'background.paper',
                p: 1.5,
              }}
            >
              <Button
                fullWidth
                onClick={() => onSelectAmenity(String(amenity.id))}
                sx={{
                  color: 'text.primary',
                  justifyContent: 'flex-start',
                  p: 0,
                  textAlign: 'left',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent' },
                }}
                variant="text"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }} noWrap>
                    {amenity.name}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {amenity.requires_approval ? 'Approval required' : 'Instant request'}
                    {amenity.max_guests ? ` · up to ${amenity.max_guests} guests` : ''}
                  </Typography>
                </Box>
              </Button>

              {feeLabel && (
                <Typography color="text.secondary" sx={{ display: 'block', mt: 0.75 }} variant="caption">
                  {feeLabel}
                </Typography>
              )}

              <Button
                fullWidth
                onClick={() => onReserve(String(amenity.id))}
                size="small"
                sx={{ mt: 1.25, textTransform: 'none', borderRadius: 1.5 }}
                variant={isSelected ? 'contained' : 'outlined'}
              >
                Reserve this amenity
              </Button>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
